import { WavingHand } from '@mui/icons-material';
import { Box, Grid2, Paper, styled, Typography, useTheme } from '@mui/material';
import { type FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts';
import {
  CommentType,
  type ExtendedCommentModel,
  type FaultData,
  type FaultInteractionData,
  markFaultAsResolved,
  postResolveFault
} from '../../models';
import { AvaniButton } from '../Buttons';
import { AvaniPaperCard } from '../Cards';
import { AmsPartScrollGallery, MediasScrollableGallery, OverflowingContent } from '../DataDisplay';
import { FaultInteractionsDropdown } from '../Dropdowns';
import { OpenAvaniShop } from '../Functions';
import { AvaniMarkResolvedDialog } from './AvaniMarkResolvedDialog';

type FaultDetailsProps = {
  faultData: FaultData;
};

type TabPanelProps = {
  index: number;
  value: number;
  faultData: FaultData;
  activeInteraction: FaultInteractionData;
};

const FaultButton = styled(Paper)(
  ({ theme }) => `
  padding: 12px;
  border-radius: 16px;
  background-color: ${theme.colors.avani.turc};
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
  flex-basis: 0;
  cursor: pointer;
  margin-top: 9px;
`
);

const PaperCard = styled(Paper)(({ theme }) => ({
  [`& `]: {
    borderRadius: '16px',
    padding: '16px',
    backgroundColor: theme.colors.avani.lightTurc,
    width: '100%',
    height: '100%'
  }
}));

const FaultDetailsPanel = (props: TabPanelProps) => {
  const { value, index, faultData, activeInteraction } = props;
  const theme = useTheme();

  const { amsPartsUsed } = faultData;
  return (
    <>
      {value === index && (
        <Grid2 container columns={4} spacing={1}>
          <Grid2 size={{ xs: 4, lg: 2 }} sx={{ display: 'flex', flexDirection: 'column' }}>
            <PaperCard sx={{ marginBottom: '9px', minHeight: '170px', maxHeight: '170px' }}>
              <Typography variant={'h3'} color={theme.colors.avani.teal} sx={{ paddingBottom: '18px' }}>
                Description
              </Typography>
              <OverflowingContent textContent={activeInteraction?.description ?? ''} heightSpan={80} />
            </PaperCard>
            <PaperCard sx={{ minHeight: '170px', maxHeight: '170px' }}>
              <Typography variant={'h3'} color={theme.colors.avani.teal} sx={{ paddingBottom: '18px' }}>
                Recommendation
              </Typography>
              <OverflowingContent textContent={activeInteraction?.actionRecommendation ?? ''} heightSpan={80} />
            </PaperCard>
          </Grid2>
          <Grid2 size={{ xs: 4, lg: 2 }} sx={{ display: 'flex', flexDirection: 'column' }}>
            <PaperCard sx={{ marginBottom: '9px', minHeight: '170px', maxHeight: '170px' }}>
              <Typography variant={'h3'} color={theme.colors.avani.teal} sx={{ paddingBottom: '18px' }}>
                Multimedia
              </Typography>
              <MediasScrollableGallery medias={activeInteraction?.medias} keepHeight />
            </PaperCard>
            <PaperCard sx={{ minHeight: '170px', maxHeight: '170px' }}>
              <Typography variant={'h3'} color={theme.colors.avani.teal} sx={{ paddingBottom: '18px' }}>
                Ams Parts
              </Typography>
              <div style={{ position: 'relative', width: '100%', minHeight: '60px', overflowX: 'auto' }}>
                <AmsPartScrollGallery amsPartsUsed={amsPartsUsed} keepHeight />
              </div>
            </PaperCard>
          </Grid2>
        </Grid2>
      )}
    </>
  );
};

export const FaultDetails: FC<FaultDetailsProps> = ({ faultData }) => {
  const { id, isResolved, isPendingRevision, comments, faultInteractions } = faultData;
  const theme = useTheme();
  const navigate = useNavigate();
  const { auth } = useAuthContext();
  const [value, setValue] = useState(0);
  const [activeInteraction, setActiveInteraction] = useState<FaultInteractionData | null>(null);

  const [message, setMessage] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);

  useEffect(() => {
    setActiveInteraction(faultInteractions[0] ?? null);
    //todo:v2-changes
  }, []);

  const attemptResolveFault = async (comment: string | null) => {
    if (!auth) return;

    const commentObj: ExtendedCommentModel = {
      content: comment ?? '',
      type: isResolved ? CommentType.FaultClientPostResolution : CommentType.FaultClientResolution,
      author: auth,
      authorId: auth.id,
      faultId: id,
      id: `${Date.now()}.${Math.random().toString().substring(2)}`
    };

    if (isResolved) await postResolveFault(id, commentObj);
    else await markFaultAsResolved(id, commentObj);

    setMessage(isResolved ? 'Post Resolve comment posted!' : 'Fault marked as resolved successfully!');
  };

  const onCancel = () => {
    setActionOpen(false);
    navigate(0);
  };

  const hasPostResolutionComment = comments.some(({ type }) => type === CommentType.FaultClientPostResolution);

  return (
    <>
      {message ? (
        <Box sx={{ p: 3, minWidth: '450px', display: 'flex', flexDirection: 'column', alignContent: 'end' }}>
          <Typography variant={'body1'} textAlign={'center'} sx={{ pb: 3 }}>
            {message}
          </Typography>
          <AvaniButton loading={false} label={'Close'} onClick={() => onCancel()} />
        </Box>
      ) : (
        <Grid2 container columns={6} mt={2} spacing={1}>
          <Grid2 size={{ xs: 6, lg: 1 }}>
            {!!faultInteractions.length && activeInteraction && (
              <Box sx={{ pb: 1 }}>
                <FaultInteractionsDropdown
                  dates
                  buttonSx={{ width: '100%', borderRadius: '16px' }}
                  faultInteractions={faultInteractions}
                  activeInteraction={activeInteraction}
                  setActiveInteraction={setActiveInteraction}
                />
              </Box>
            )}
            <Box>
              <Paper
                onClick={() => OpenAvaniShop()}
                elevation={0}
                sx={{
                  display: 'flex',
                  backgroundColor: theme.colors.avani.turc,
                  gap: '8px',
                  padding: '16px',
                  borderRadius: '16px',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <Typography variant={'body1'} sx={{ color: 'white', flexGrow: 1, textAlign: 'center' }}>
                  Products & Services
                </Typography>
              </Paper>
            </Box>
            {!isPendingRevision && !hasPostResolutionComment && (
              <Box>
                <FaultButton elevation={0} onClick={() => setActionOpen(true)}>
                  <Typography variant={'body1'} lineHeight={1} sx={{ textAlign: 'center', mt: '5px' }}>
                    <WavingHand sx={{ color: 'white' }} />
                  </Typography>
                  <Typography variant={'body1'} sx={{ color: 'white', textAlign: 'center' }}>
                    {isResolved ? 'Post Comment' : 'Mark Resolved'}
                  </Typography>
                </FaultButton>
                <AvaniMarkResolvedDialog
                  toggle={actionOpen}
                  title={isResolved ? 'Post Comment' : 'Mark Resolved'}
                  onCancel={() => setActionOpen(false)}
                  onResolve={attemptResolveFault}
                />
              </Box>
            )}
          </Grid2>
          <Grid2 size={{ xs: 6, lg: 5 }}>
            {faultInteractions.map((interaction, ind) => {
              if (!activeInteraction) return;
              return (
                <FaultDetailsPanel
                  value={value}
                  index={ind}
                  faultData={faultData}
                  activeInteraction={activeInteraction}
                  key={interaction.startedAt.toString() + ind}
                />
              );
            })}
          </Grid2>
          {!!comments.length && (
            <Grid2 size={{ xs: 6 }}>
              {!!comments &&
                comments.map((comment, i) => {
                  return <AvaniPaperCard outline key={i} {...comment} />;
                })}
            </Grid2>
          )}
        </Grid2>
      )}
    </>
  );
};
